// The consent boxes on a form that creates an account.
//
// Three things here are deliberate and should survive a redesign:
//
//   1. Every box starts unticked. A pre-ticked box is not the clear
//      affirmative action s.6(1) asks for.
//   2. The essential purpose and the optional ones are separate boxes. One
//      blanket tick covering the account and marketing would be consent
//      "beyond what is necessary", which s.6(2) voids.
//   3. The purpose text is shown next to the box, not hidden behind the link.
//      The link is there for the full notice; the member should not have to
//      open it to know what they are agreeing to.

import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { Box, Checkbox, FormControlLabel, FormHelperText, Link, Stack, Typography } from '@mui/material';
//
import { DOC_LABELS, LEGAL_PATHS, operatorName, registrationPurposes } from './legalCopy';

// ----------------------------------------------------------------------

ConsentChecklist.propTypes = {
  org: PropTypes.object,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  colour: PropTypes.string,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};

export default function ConsentChecklist({ org, value, onChange, error, colour, disabled, sx }) {
  const purposes = registrationPurposes(org);
  const name = operatorName(org);

  const toggle = (code) => (event) => {
    onChange({ ...(value || {}), [code]: event.target.checked });
  };

  return (
    <Box sx={sx}>
      <Stack spacing={1.25}>
        {purposes.map((purpose) => {
          const isEssential = Boolean(purpose.essential);
          const showError = Boolean(error) && isEssential && !value?.[purpose.code];

          return (
            <FormControlLabel
              key={purpose.code}
              disabled={disabled}
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={
                <Checkbox
                  checked={Boolean(value?.[purpose.code])}
                  onChange={toggle(purpose.code)}
                  size="small"
                  sx={{
                    mt: -0.5,
                    ...(colour && {
                      '&.Mui-checked': { color: colour },
                    }),
                    ...(showError && { color: 'error.main' }),
                  }}
                />
              }
              label={
                <Box sx={{ pt: 0.25 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                    {isEssential ? (
                      <>
                        I agree to {name}&apos;s{' '}
                        <Link
                          component={RouterLink}
                          to={LEGAL_PATHS.terms}
                          target="_blank"
                          rel="noopener"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: colour || 'primary.main' }}
                        >
                          {DOC_LABELS.terms}
                        </Link>{' '}
                        and{' '}
                        <Link
                          component={RouterLink}
                          to={LEGAL_PATHS.privacy}
                          target="_blank"
                          rel="noopener"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: colour || 'primary.main' }}
                        >
                          {DOC_LABELS.privacy}
                        </Link>
                      </>
                    ) : (
                      purpose.label
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.5 }}
                  >
                    {purpose.description}
                    {!isEssential && ' You can turn this off at any time.'}
                  </Typography>
                </Box>
              }
            />
          );
        })}
      </Stack>

      {error && (
        <FormHelperText error sx={{ mt: 1, mx: 0 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
}
